import PropTypes from "prop-types";
import { Link, useNavigate } from "react-router-dom";
import clsx from "clsx";
import { useDispatch } from "react-redux";
import { logoutUser } from "../../../../redux/WithcookieSlice";
import { useTranslation } from "../../../../redux/TranslationHelper";

const NavMenu = ({ menuWhiteClass, sidebarMenu, menus, textStyle, isLoggedIn = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // menus 배열이 존재하고 비어있지 않은지 확인
  const hasCustomMenus = menus && menus.length > 0;
  
  // 링크 스타일 설정
  const linkStyle = textStyle || {};

  // 메뉴 클릭 핸들러
  const handleMenuClick = (e, menu) => {
    e.preventDefault();
    
    // 메뉴 액션 처리
    if (menu.menuAction === 'logout') {
      dispatch(logoutUser()).then(() => {
        // 로그아웃 성공 후 홈 페이지로 이동
        navigate('/');
      });
      return;
    }
    
    // 외부 링크 처리
    if (menu.externalLink) {
      window.open(menu.externalLink, '_blank');
      return;
    }
    
    // 일반 내부 링크 처리
    navigate(process.env.PUBLIC_URL + menu.path);
  };
  
  // 루트 메뉴 항목(parentId가 null)과 하위 메뉴 항목 구성 함수
  const renderMenuItems = () => {
    if (!hasCustomMenus) {
      // 기본 메뉴 렌더링
      return (
        <>
          <li>
            <Link to={process.env.PUBLIC_URL + "/"} style={linkStyle}>
              {t("홈")}
              {sidebarMenu ? (
                <span>
                  <i className="fa fa-angle-right"></i>
                </span>
              ) : (
                <i className="fa fa-angle-down" />
              )}
            </Link>
          </li>
          <li>
            <Link to={process.env.PUBLIC_URL + "/shopping"} style={linkStyle}>
              {t("쇼핑")}
              {sidebarMenu ? (
                <span>
                  <i className="fa fa-angle-right"></i>
                </span>
              ) : (
                <i className="fa fa-angle-down" />
              )}
            </Link>
          </li>
          <li>
            <Link to={process.env.PUBLIC_URL + "/cart"} style={linkStyle}>
              {t("장바구니")}
            </Link>
          </li>
          <li>
            <Link to={process.env.PUBLIC_URL + "/my-account"} style={linkStyle}>
              {t("마이페이지")}
            </Link>
          </li>
          <li>
            <Link to={process.env.PUBLIC_URL + "/contact"} style={linkStyle}>
              {t("고객센터")}
            </Link>
          </li>
        </>
      );
    } else {
      // 커스텀 메뉴 렌더링
      // 메뉴 항목 정렬 (order 기준)
      const sortedMenus = [...menus].sort((a, b) => a.order - b.order);
      
      // parentId가 null인 메인 메뉴 항목만 필터링 (글로벌 메뉴 제외)
      const mainMenus = sortedMenus.filter(
        menu => menu.parentId === null && 
        menu.visible && 
        menu.type !== 'GLOBAL' && // 글로벌 메뉴는 표시하지 않음
        (
          (!menu.is_logged && !menu.is_not_logged) || // 로그인 관련 속성이 없는 경우 모두 표시
          (menu.is_logged && isLoggedIn) ||           // 로그인 필요 메뉴는 로그인 시만 표시
          (menu.is_not_logged && !isLoggedIn)         // 비로그인 메뉴는 로그아웃 시만 표시
        )
      );
      
      // "/" 경로를 가진 홈 메뉴가 있는지 확인
      const hasHomeMenu = mainMenus.some(menu => menu.path === '/');
      
      // 홈 메뉴가 없으면 기본 홈 메뉴 추가
      const menuElements = [];
      
      
      // 나머지 메뉴 항목 추가
      mainMenus.forEach(menu => {
        // 현재 메뉴의 하위 메뉴 항목 찾기 (글로벌 메뉴의 자식도 제외)
        const subMenus = sortedMenus.filter(
          item => item.parentId === menu.id && 
          item.visible && 
          item.type !== 'GLOBAL' &&
          (
            (!item.is_logged && !item.is_not_logged) || // 로그인 관련 속성이 없는 경우 모두 표시
            (item.is_logged && isLoggedIn) ||           // 로그인 필요 메뉴는 로그인 시만 표시
            (item.is_not_logged && !isLoggedIn)         // 비로그인 메뉴는 로그아웃 시만 표시
          )
        );
        const hasSubMenu = subMenus.length > 0;
        
        menuElements.push(
          <li key={menu.id}>
            {menu.externalLink || menu.menuAction ? (
              // 외부 링크나 특수 액션이 있는 메뉴
              <a 
                href="#" 
                onClick={(e) => handleMenuClick(e, menu)} 
                style={linkStyle}
              >
                {t(menu.name)}
                {hasSubMenu && (
                  sidebarMenu ? (
                    <span>
                      <i className="fa fa-angle-right"></i>
                    </span>
                  ) : (
                    <i className="fa fa-angle-down" />
                  )
                )}
              </a>
            ) : (
              // 일반 내부 링크 메뉴
              <Link to={process.env.PUBLIC_URL + menu.path} style={linkStyle}>
                {/* 기본값 처리 */}
                {t(menu.name || '메뉴')}
                {hasSubMenu && (
                  sidebarMenu ? (
                    <span>
                      <i className="fa fa-angle-right"></i>
                    </span>
                  ) : (
                    <i className="fa fa-angle-down" />
                  )
                )}
              </Link>
            )}
            {hasSubMenu && (
              <ul className="submenu">
                {subMenus.map(subMenu => (
                  <li key={subMenu.id}>
                    {subMenu.externalLink || subMenu.menuAction ? (
                      // 외부 링크나 특수 액션이 있는 서브메뉴
                      <a 
                        href="#" 
                        onClick={(e) => handleMenuClick(e, subMenu)}
                      >
                        {t(subMenu.name)}
                      </a>
                    ) : (
                      // 일반 내부 링크 서브메뉴
                      <Link to={process.env.PUBLIC_URL + subMenu.path}>
                        {/* 서브메뉴 이름도 동일하게 처리 */}
                        {t(subMenu.name || '서브메뉴')}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      });
      
      return menuElements;
    }
  };
  
  return (
    <div
      className={clsx(sidebarMenu
          ? "sidebar-menu"
          : `main-menu ${menuWhiteClass ? menuWhiteClass : ""}`)}
      style={textStyle}
    >
      <nav>
        <ul>
          {renderMenuItems()}
        </ul>
      </nav>
    </div>
  );
};

NavMenu.propTypes = {
  menuWhiteClass: PropTypes.string,
  sidebarMenu: PropTypes.bool,
  menus: PropTypes.array,
  textStyle: PropTypes.object,
  isLoggedIn: PropTypes.bool
};

export default NavMenu;
